import * as Sentry from '@sentry/vue'

/**
 * Returns a filename that does not collide with `taken`, appending " (2)", " (3)", … before
 * the extension when needed. Mutates `taken` to include the returned name so repeated calls
 * stay consistent within one ZIP.
 *
 * Without this, JSZip's `zip.file(name, blob)` silently overwrites earlier entries when two
 * documents share a filename — the bug from SHP-723.
 */
export const uniqueZipFilename = (filename: string, taken: Set<string>): string => {
  if (!taken.has(filename)) {
    taken.add(filename)
    return filename
  }

  const dot = filename.lastIndexOf('.')
  const stem = dot > 0 ? filename.slice(0, dot) : filename
  const ext = dot > 0 ? filename.slice(dot) : ''

  let counter = 2
  let candidate = `${stem} (${counter})${ext}`
  while (taken.has(candidate)) {
    counter += 1
    candidate = `${stem} (${counter})${ext}`
  }
  taken.add(candidate)
  return candidate
}

export type DocumentToZip = { filename: string; url: string }

export type ZipBuildResult = {
  zipBlob: Blob | null
  failed: string[]
  successCount: number
}

type JSZipLike = {
  file: (name: string, data: Blob) => void
  generateAsync: (options: { type: 'blob' }) => Promise<Blob>
}

export type BuildZipDeps = {
  fetch: typeof fetch
  createZip: () => JSZipLike
  /** Override only in tests — real callers use the default. */
  retryDelayMs?: number
}

const RETRY_DELAY_MS = 500

const fetchBlobOnce = async (url: string, fetchFn: typeof fetch): Promise<Blob> => {
  const response = await fetchFn(url)
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  return response.blob()
}

/**
 * Fetches each document and packs successful responses into a single ZIP. Failed fetches are
 * retried once after a short delay to ride out transient network/5xx blips, then collected by
 * filename instead of aborting the batch. Returns `zipBlob: null` when every fetch failed, so
 * the caller can skip the download step entirely.
 *
 * Collision-safe naming is delegated to `uniqueZipFilename` so two docs with the same
 * filename both end up in the archive.
 */
export const buildZipFromDocuments = async (
  documents: DocumentToZip[],
  deps: BuildZipDeps
): Promise<ZipBuildResult> => {
  const zip = deps.createZip()
  const usedNames = new Set<string>()
  const failed: string[] = []
  const retryDelayMs = deps.retryDelayMs ?? RETRY_DELAY_MS

  // Reserve the disambiguated display name up front so the ZIP entry and the failure
  // toast reference the same name when two selected documents share a filename.
  const planned = documents.map((doc) => ({
    doc,
    displayName: uniqueZipFilename(doc.filename, usedNames)
  }))

  const fetchResults = await Promise.all(
    planned.map(async ({ doc, displayName }) => {
      try {
        return { displayName, blob: await fetchBlobOnce(doc.url, deps.fetch) }
      } catch {
        try {
          await new Promise((resolve) => setTimeout(resolve, retryDelayMs))
          return { displayName, blob: await fetchBlobOnce(doc.url, deps.fetch) }
        } catch (retryError) {
          Sentry.captureException(retryError, {
            tags: { feature: 'bulk-zip-download' },
            extra: { filename: displayName }
          })
          return { displayName, blob: null as Blob | null }
        }
      }
    })
  )

  for (const { displayName, blob } of fetchResults) {
    if (blob === null) {
      failed.push(displayName)
      continue
    }
    zip.file(displayName, blob)
  }

  const successCount = documents.length - failed.length
  if (successCount === 0) {
    return { zipBlob: null, failed, successCount: 0 }
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' })
  return { zipBlob, failed, successCount }
}
